<template>
  <main class="main">
    <div class="anim-wrap">
      <div class="container test-container">
        <div
          v-if="testActive"
          class="row"
          :class="{
            selected: selected === true || selected === false,
            out: !showQuestions,
          }"
        >
          <div class="col-md-6" :class="formattedQuestions[0].order[0]">
            <div
              class="question-image question-image-1"
              :class="{ toggled: selected === false }"
              @click="selectAnswer(false)"
            >
              <div
                v-if="formattedQuestions[0]"
                :class="{
                  toggled: closedCaptions === true,
                  dark: formattedQuestions[0].scheme === 'dark',
                }"
                class="caption-container"
              >
                {{ formattedQuestions[0].captions[0] }}
              </div>
              <div class="load-bar"></div>
            </div>
          </div>
          <div class="col-md-6" :class="formattedQuestions[0].order[1]">
            <div
              class="question-image question-image-2"
              :class="{ toggled: selected === true }"
              @click="selectAnswer(true)"
            >
              <div
                v-if="formattedQuestions[0]"
                :class="{
                  toggled: closedCaptions === true,
                  dark: formattedQuestions[0].scheme === 'dark',
                }"
                class="caption-container"
              >
                {{ formattedQuestions[0].captions[1] }}
              </div>
              <div class="load-bar"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="info-modal"
      :class="{
        toggled: showInfo === true,
      }"
    >
      <div class="container">
        <div class="row">
          <div class="col-md-6 offset-md-3">
            <div class="text-center test-information">
              <div class="wrap">
                <img src="@/assets/example_item.png" />
              </div>

              <h3>Information</h3>
              <p>
                In the test you will be presented multiple pairs of pictures.
                Each time, you need to select which one of the pictures
                describes you the most.
              </p>
              <p>
                You can use the bottom menu to control closed captions or undo
                previous item selection at any time.
              </p>
              <div v-if="$hasCompletedPersonality()" class="warning">
                After completing the test, the new personality report will
                replace your previous report and you won't be able to access the
                old report.
              </div>
              <button @click="closeInfo" class="btn btn-primary">
                Get started!
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="loader" :class="{ toggled: loading === true }">
      <div class="wrap" v-if="endTest === false">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <p>{{ submitMessage }}</p>
    </div>
    <div class="question-controls" :class="{ toggled: testActive === true }">
      <button
        class="tab tab-info"
        v-on:click="showInfo = !showInfo"
        title="Info"
        v-tippy
      >
        <font-awesome-icon icon="info" />
      </button>
      <button
        v-on:click="undoSelection"
        class="tab tab-undo"
        :class="{ unclickable: undoActive === false || answeredQuestions < 1 }"
        title="Undo last selection"
        v-tippy
      >
        <font-awesome-icon icon="undo" />
      </button>
      <button
        class="tab tab-cc"
        title="Closed captions"
        v-tippy
        v-on:click="closedCaptions = !closedCaptions"
        :class="{ toggled: closedCaptions === true }"
      >
        <font-awesome-icon icon="closed-captioning" />
      </button>
    </div>
    <div class="progress-counter" :class="{ toggled: testActive === true }">
      <div
        v-for="index in testLength"
        :key="index"
        :class="{ active: index <= answeredQuestions.length }"
        class="progress-dot"
      ></div>
    </div>
    <button v-on:click="runDebug" class="btn btn-debug">Debug</button>
  </main>
</template>

<script>
// FireBase
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/functions";
import { personalityCards } from "@/assets/personalityData.js";
import SVGInjector from "svg-injector";
import createjs from "createjs-cmd";

export default {
  name: "TestPersonality",
  components: {},
  computed: {},
  props: {
    code: String,
  },
  data() {
    return {
      submitMessage: "Calculating the scores",
      source: null,
      uid: null,
      peronalityData: null,
      formattedQuestions: [],
      answeredQuestions: [],
      showQuestions: false,
      loadQueue: false,
      selected: null,
      loading: false,
      endTest: false,
      success: false,
      selectionTime: 0,
      selectionClock: null,
      focusActive: false,
      focusTimer: null,
      closedCaptions: true,
      testActive: false,
      undoActive: false,
      testProgress: 0,
      testLength: 0,
      showInfo: true,
      sendCounter: 0,
      imagePath: "https://cdn.traitstack.com/personality/",
    };
  },
  mounted() {
    this.source = this.$route.query.src;
    this.uid = this.$route.query.uid;
    if (this.code || this.uid) {
      this.initTest();
    } else {
      this.showInfo = false;
    }
  },
  methods: {
    // Build test questions
    buildQuestions() {
      // Add active questions
      this.formattedQuestions = this.formattedQuestions.concat(
        personalityCards.ope.active,
        personalityCards.con.active,
        personalityCards.agr.active,
        personalityCards.ext.active,
        personalityCards.neu.active
      );

      // Shuffle
      this.$shuffleArray(this.formattedQuestions);

      // Grab research questions
      let opeExtra = personalityCards.ope.research;
      let conExtra = personalityCards.con.research;
      let extExtra = personalityCards.ext.research;
      let agrExtra = personalityCards.agr.research;
      let neuExtra = personalityCards.neu.research;

      // Shuffle research questions
      this.$shuffleArray(opeExtra);
      this.$shuffleArray(conExtra);
      this.$shuffleArray(extExtra);
      this.$shuffleArray(agrExtra);
      this.$shuffleArray(neuExtra);

      let extraQuestions = [
        opeExtra[0],
        opeExtra[1],
        conExtra[0],
        conExtra[1],
        extExtra[0],
        extExtra[1],
        agrExtra[0],
        agrExtra[1],
        neuExtra[0],
        neuExtra[1],
      ];

      this.$shuffleArray(extraQuestions);

      // Add research questions
      this.formattedQuestions = this.formattedQuestions.concat(extraQuestions);

      // Add randomised order per item
      this.formattedQuestions.forEach((part, index) => {
        this.formattedQuestions[index].order = this.randomiseOrder();
      });

      // Populate total length
      this.testLength = this.formattedQuestions.length;
    },

    // Initialise test
    initTest: function() {
      // Init loadQueue
      this.loadQueue = new createjs.LoadQueue(true);
      //this.loadQueue.on("fileload", this.handleFileComplete);

      this.buildQuestions();

      this.preloadImagePair(0);
      this.preloadImagePair(1);
    },

    closeInfo: function() {
      this.showInfo = false;
      if (!this.testActive) {
        this.startTest();
      }
    },

    startTest: function() {
      this.testActive = true;
      setTimeout(() => {
        this.renderImages();
      }, 1000);
    },

    timerStart: function() {
      this.selectionClock = new Date();
    },

    getSelectionTime() {
      let currentTime = new Date().getTime();
      let timeDifference = currentTime - this.selectionClock.getTime();
      let timeDifferenceInSeconds = timeDifference / 1000;
      return timeDifferenceInSeconds;
    },

    // Preload image pair
    preloadImagePair(orderNumber) {
      this.loadQueue.loadFile({
        src:
          this.imagePath +
          this.formattedQuestions[orderNumber].files[0] +
          ".svg",
        type: createjs.IMAGE,
      });
      this.loadQueue.loadFile({
        src:
          this.imagePath +
          this.formattedQuestions[orderNumber].files[1] +
          ".svg",
        type: createjs.IMAGE,
      });
    },

    /* // Debug
    handleFileComplete: function(event) {
      //console.log("asset loaded" + event);
    }, */

    // Select answer
    selectAnswer(option) {
      this.selected = option;
      this.undoActive = false;
      this.setChoiceSelectionTime();
    },

    // Clear selection timer
    clearChoiceSelectionTime: function() {
      this.focusActive = false;
      clearInterval(this.focusTimer);
    },

    // Set selection timer
    setChoiceSelectionTime: function() {
      this.clearChoiceSelectionTime();
      setTimeout(() => {
        this.focusActive = true;
      }, 10);

      this.focusTimer = setInterval(() => {
        this.clearChoiceSelectionTime();
        this.saveSelection();
      }, 1000);
    },

    // Render test image pair
    renderImages() {
      document.querySelectorAll(".injected-svg").forEach((e) => e.remove());

      var questionImage1 = document.querySelector(".question-image-1");
      var newImage1 = document.createElement("img");
      newImage1.setAttribute("class", "svg-image");
      newImage1.setAttribute(
        "data-src",
        this.imagePath + this.formattedQuestions[0].files[0] + ".svg"
      );
      questionImage1.appendChild(newImage1);

      var questionImage2 = document.querySelector(".question-image-2");
      var newImage2 = document.createElement("img");
      newImage2.setAttribute("class", "svg-image");
      newImage2.setAttribute(
        "data-src",
        this.imagePath + this.formattedQuestions[0].files[1] + ".svg"
      );
      questionImage2.appendChild(newImage2);

      var mySVGsToInject = document.querySelectorAll(".svg-image");
      SVGInjector(mySVGsToInject);

      if (!this.undoState) {
        this.randomiseOrder();
      }

      // Preload the third image in the queue
      const imageToPreload = 2;
      if (imageToPreload < this.formattedQuestions.length) {
        this.preloadImagePair(imageToPreload);
      }

      setTimeout(() => {
        this.timerStart();
        this.showQuestions = true;
      }, 250);
    },

    // Return random order strings (classes)
    randomiseOrder() {
      var random = Math.random() >= 0.5;
      let orderClass = [];
      if (random) {
        orderClass = [
          "order-2 text-center text-md-left",
          "order-1 text-center text-md-right",
        ];
      } else {
        orderClass = ["text-center text-md-right", "text-center text-md-left"];
      }
      return orderClass;
    },

    // Undo selection
    undoSelection() {
      if (!this.undoActive) {
        return false;
      }
      this.undoActive = false;
      this.showQuestions = false;
      setTimeout(() => {
        this.formattedQuestions.unshift(
          this.answeredQuestions[this.answeredQuestions.length - 1]
        );
        this.answeredQuestions.pop();
        this.selected = null;
        this.renderImages();
      }, 750);
    },

    // Save selection
    saveSelection() {
      this.showQuestions = false;
      this.undoActive = false;
      let selectionTime = this.getSelectionTime();

      setTimeout(() => {
        let question = this.formattedQuestions[0];
        question.selection = this.selected;
        question.time = selectionTime;
        this.answeredQuestions.push(question);
        this.formattedQuestions.shift();
        this.selected = null;

        if (this.formattedQuestions.length > 0) {
          this.renderImages();
        } else {
          this.completeTest();
        }

        setTimeout(() => {
          this.undoActive = true;
        }, 500);
      }, 750);
      //console.log(this.answeredQuestions);
    },

    transformScores() {
      let transformedScores = [];

      this.answeredQuestions.forEach((question) => {
        let obj = {
          id: question.id,
          band: question.band,
          value: question.selection,
          active: question.active,
          time: question.time,
        };
        transformedScores.push(obj);
      });

      return transformedScores;
    },

    runDebug: function() {
      const debugScores = [];
      this.formattedQuestions.forEach((item) => {
        let nmbr = Math.floor(Math.random() * 2) + 1;
        let value = false;
        if (nmbr === 1) {
          value = true;
        }
        let obj = {
          id: item.id,
          band: item.band,
          value: value,
          active: item.active,
          time: 1,
        };
        debugScores.push(obj);
      });

      this.testActive = false;
      this.loading = true;
      this.sendTestData(debugScores);
    },

    // Complete test
    completeTest: function() {
      this.testActive = false;
      this.loading = true;
      const scores = this.transformScores();
      this.sendTestData(scores);
    },

    sendTestData: function(scores) {
      let storeInsightsScore = firebase
        .functions()
        .httpsCallable("storeInsightsScore");

      storeInsightsScore({
        scores: scores,
        code: this.code,
        source: this.source,
        uid: this.uid,
      })
        .then(() => {
          if (this.source && this.uid) {
            this.submitMessage =
              "Assessment completed! You will be now redirected back.";
            this.endTest = true;
            window.location.replace("https://portal.aaafc.com.au/my-account/");
          } else {
            this.loading = false;
            setTimeout(() => {
              this.$router.replace({ path: "/hub?code=" + this.code });
            }, 500);
          }
        })
        .catch((error) => {
          if (this.sendCounter < 2) {
            this.sendCounter++;
            this.sendTestData(scores);
          } else {
            var message = error.message;
            console.log(message);
          }
        });
    },
  },
};
</script>
